import { Controller } from "@hotwired/stimulus"

/**
* Stimulus controller for creating a floating table header that sticks to top on scroll
* Maintains column alignment and horizontal scroll sync with original table
*
* Usage:
* <div data-controller="floating-header" data-floating-header-target="tableContainer">
*   <table data-floating-header-target="table">
*     <thead>...</thead>
*     <tbody>...</tbody>
*   </table>
* </div>
*/
export default class extends Controller {
  static targets = ["table", "tableContainer"]

  connect() {
    if (!this.hasTableTarget) {
      return;
    }
    this.createFloatingHeader()
    this.bindEvents()
    this.syncColumnWidths()
    this.checkForSortedColumn()

    this.observer = new MutationObserver(this.handleMutation.bind(this))
    const recordsContainer = document.querySelector('#records')
    if (recordsContainer) {
      this.observer.observe(recordsContainer, {
        childList: true,
        subtree: true
      })
    }
  }

  disconnect() {
    if (!this.hasTableTarget) {
      return;
    }
    this.removeFloatingHeader()
    this.unbindEvents()
    if (this.observer) {
     this.observer.disconnect()
    }
  }

  createFloatingHeader() {
    this.floatingHeader = document.createElement('div')
    this.floatingHeader.className = 'floating-table-header'
    this.floatingHeader.style.cssText = `
      position: fixed;
      top: 70px;
      left: 0;
      right: 0;
      z-index: 1030;
      background: white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      display: none;
      overflow: hidden;
    `

    this.floatingHeaderScroll = document.createElement('div')
    this.floatingHeaderScroll.style.cssText = `
      overflow-x: hidden;
      overflow-y: hidden;
    `

    const originalTable = this.tableTarget
    this.floatingTable = originalTable.cloneNode(false)
    this.floatingTable.style.marginBottom = '0'
    
    const originalThead = originalTable.querySelector('thead')
    if (originalThead) {
      this.floatingThead = originalThead.cloneNode(true)
      this.floatingTable.appendChild(this.floatingThead)
    }

    this.floatingHeaderScroll.appendChild(this.floatingTable)
    this.floatingHeader.appendChild(this.floatingHeaderScroll)
    document.body.appendChild(this.floatingHeader)
  }

  removeFloatingHeader() {
    if (this.floatingHeader) {
      this.floatingHeader.remove()
    }
  }

  bindEvents() {
    this.handleScroll = this.handleScroll.bind(this)
    this.handleHorizontalScroll = this.handleHorizontalScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    
    window.addEventListener('scroll', this.handleScroll)
    this.tableContainerTarget.addEventListener('scroll', this.handleHorizontalScroll)
    window.addEventListener('resize', this.handleResize)
  }

  unbindEvents() {
    window.removeEventListener('scroll', this.handleScroll)
    this.tableContainerTarget.removeEventListener('scroll', this.handleHorizontalScroll)
    window.removeEventListener('resize', this.handleResize)
  }

  handleScroll() {
    if (!this.floatingHeader) return

    const tableRect = this.tableTarget.getBoundingClientRect()
    const shouldShow = tableRect.top < 70

    if (shouldShow && this.floatingHeader.style.display === 'none') {
      this.floatingHeader.style.display = 'block'
      this.syncFloatingHeader()
    } else if (!shouldShow && this.floatingHeader.style.display === 'block') {
      this.floatingHeader.style.display = 'none'
    }
  }

  handleHorizontalScroll(event) {
    if (!this.floatingHeader || this.floatingHeader.style.display === 'none') return
    
    this.floatingHeaderScroll.scrollLeft = event.target.scrollLeft
  }

  handleResize() {
    if (this.floatingHeader && this.floatingHeader.style.display === 'block') {
      this.syncFloatingHeader()
    }
  }

  syncFloatingHeader() {
    this.syncPosition()
    this.syncColumnWidths()
    this.syncHorizontalScroll()
  }

  syncPosition() {
    const containerRect = this.tableContainerTarget.getBoundingClientRect()
    
    this.floatingHeader.style.left = containerRect.left + 'px'
    this.floatingHeader.style.width = containerRect.width + 'px'
    this.floatingHeader.style.right = 'auto'
  }

  syncColumnWidths() {
    const originalCells = this.tableTarget.querySelectorAll('thead th')
    const floatingCells = this.floatingTable.querySelectorAll('thead th')

    this.floatingTable.style.width = this.tableTarget.offsetWidth + 'px'
    this.floatingTable.style.minWidth = this.tableTarget.style.minWidth || 'max-content'

    originalCells.forEach((originalCell, index) => {
      if (floatingCells[index]) {
        const width = originalCell.offsetWidth
        floatingCells[index].style.width = width + 'px'
        floatingCells[index].style.minWidth = width + 'px'
        floatingCells[index].style.maxWidth = width + 'px'
      }
    })
  }

  syncHorizontalScroll() {
    this.floatingHeaderScroll.scrollLeft = this.tableContainerTarget.scrollLeft
  }

  checkForSortedColumn() {
    const sortedHeader = this.findSortedColumn()
    if (sortedHeader) {
      this.scrollToColumn(sortedHeader)
    }
  }

  findSortedColumn() {
    const urlParams = new URLSearchParams(window.location.search)
    const sortParam = urlParams.get('sort')
    const directionParam = urlParams.get('direction')

    if (!sortParam || !directionParam) {
      return null
    }

    const headers = this.tableTarget.querySelectorAll('thead th')

    for (const header of headers) {
      const link = header.querySelector('a[data-sort-name]')
      if (link && link.dataset.sortName === sortParam) {
        return header
      }
    }

    return null
  }

  scrollToColumn(columnHeader) {
    const containerRect = this.tableContainerTarget.getBoundingClientRect()
    const columnRect = columnHeader.getBoundingClientRect()

    const columnLeftRelativeToContainer = columnRect.left - containerRect.left + this.tableContainerTarget.scrollLeft
    const columnRightRelativeToContainer = columnLeftRelativeToContainer + columnRect.width

    const containerWidth = this.tableContainerTarget.clientWidth
    const currentScrollLeft = this.tableContainerTarget.scrollLeft
    const viewportLeft = currentScrollLeft
    const viewportRight = currentScrollLeft + containerWidth

    // Check if column is already fully visible
    if (columnLeftRelativeToContainer >= viewportLeft && columnRightRelativeToContainer <= viewportRight) {
      return // Column is already visible, don't scroll
    }

    // Center the column in the viewport
    const columnCenter = columnLeftRelativeToContainer + (columnRect.width / 2)
    const targetScrollLeft = columnCenter - (containerWidth / 2)

    this.tableContainerTarget.scrollLeft = Math.max(0, targetScrollLeft)
  }

  handleMutation(mutations) {
   const hasNewRows = mutations.some(mutation => 
     mutation.type === 'childList' && mutation.addedNodes.length > 0
   )
   
   if (hasNewRows) {
     this.refresh()
     this.checkForSortedColumn()
   }
  }

  refresh() {
    if (this.floatingHeader) {
      this.removeFloatingHeader()
      this.createFloatingHeader()
      this.syncColumnWidths()
    }
  }
}
